/**
 * Generic image upload orchestration helper.
 *
 * This module provides a reusable pattern for the 3-step image upload flow:
 * 1. Get upload path from server (Cloud Function)
 * 2. Upload file to Firebase Storage
 * 3. Finalize upload with server (Cloud Function)
 *
 * The helper is dependency-free - callers provide Firebase-specific implementations
 * via callbacks, allowing use in both web (firebase SDK) and server (firebase-admin) contexts.
 */
/**
 * Orchestrates the 3-step image upload flow.
 *
 * @example
 * ```typescript
 * // In web-spa with Firebase JS SDK:
 * const result = await uploadImage({
 *   file: imageFile,
 *   getFileName: (f) => f.name,
 *   getContentType: (f) => f.type,
 *   getUploadPath: async (fileName, contentType) => {
 *     const { storagePath } = await callFunctions("getRecipeImageUploadPath", {
 *       recipeId, fileName, contentType
 *     });
 *     return storagePath;
 *   },
 *   uploadToStorage: async (file, path, contentType) => {
 *     const storageRef = ref(storage, path);
 *     await uploadBytes(storageRef, file, { contentType });
 *     return getDownloadURL(storageRef);
 *   },
 *   finalizeUpload: async (storagePath, imageUrl) => {
 *     await callFunctions("finalizeRecipeImage", { recipeId, storagePath, imageUrl });
 *   },
 * });
 * ```
 *
 * @param config - Configuration with callbacks for each step
 * @returns The storage path and download URL of the uploaded image
 * @throws Error if any step fails
 */
export async function uploadImage(config) {
    const { file, getFileName, getContentType, getUploadPath, uploadToStorage, finalizeUpload } = config;
    const fileName = getFileName(file);
    const contentType = getContentType?.(file) || "image/jpeg";
    // Step 1: Get the upload path from the server
    const storagePath = await getUploadPath(fileName, contentType);
    // Step 2: Upload the file to storage
    const imageUrl = await uploadToStorage(file, storagePath, contentType);
    // Step 3: Finalize the upload (update Firestore)
    await finalizeUpload(storagePath, imageUrl);
    return { storagePath, imageUrl };
}
